import Track from './Track';
import Idea from './Idea';
import User from './User';
import firebase from '../firebaseConfig';

class Activity {
    constructor(id, metadata, notificationType, timeCreated, project, senderName) {
        this.id = id;
        this.metadata = metadata;
        this.notificationType = notificationType;
        this.timeCreated = timeCreated;
        this.project = project;
        this.senderName = senderName;
    }

    trackActivity = [
        "added-track",
        "deleted-track",
        "renamed-track",
        "added-revision-track",
        "deleted-revision-track",
        "changed-revision-track",
        "commented-track",
    ];

    ideaActivity = [
        "added-idea",
        "deleted-idea",
        "renamed-idea",
    ];

    artActivity = [
        "added-art",
        "deleted-art"
    ];

    projectActivity = [
        "added-project",
        "deleted-project",
        "renamed-project",
        "added-project-album-art",
        "updated-project-album-art",
        "removed-project-album-art",
    ]

    getSenderName() {
        if (this.metadata.senderId === firebase.auth().currentUser.uid) {
            return "You"
        } else {
            return this.senderName;
        }
    }

    async getLabel() {
        try {
            if (this.trackActivity.includes(this.notificationType)) {
                return await this.getLabelForTrackActivity();
            } else if (this.ideaActivity.includes(this.notificationType)) {
                return await this.getLabelForIdeaActivity();
            } else if (this.artActivity.includes(this.notificationType)) {
                return await this.getLabelForArtActivity();
            } else if (this.projectActivity.includes(this.notificationType)) {
                return await this.getLabelForProjectActivity();
            }
            return this.notificationType;
        } catch {
            return this.getDegradedLabel();
        }
    }

    // Lookup-free fallback when label resolution fails — keeps the row off an eternal skeleton.
    getDegradedLabel() {
        const who = this.getSenderName();
        const project = this.project?.name || 'a project';
        if (this.ideaActivity.includes(this.notificationType)) {
            return `${who} updated an idea in ${project}`;
        }
        if (this.trackActivity.includes(this.notificationType)) {
            return this.notificationType === 'commented-track'
                ? `${who} commented on a track in ${project}`
                : `${who} updated a track in ${project}`;
        }
        return `${who} updated ${project}`;
    }

    async getLabelForArtActivity() {
        switch (this.notificationType) {
            case "added-art":
                return `${this.getSenderName()} added art to ${this.project.name}`;
            case "deleted-art":
                return `${this.getSenderName()} deleted art from ${this.project.name}`;
            default:
                return this.notificationType;
        }
    }

    // Track display name, or '[deleted]' when it's gone from the live tree
    // and deletedData — lets the label keep its verb either way.
    async resolveTrackName() {
        try {
            const track = await Track.getTrackById(
                this.metadata["artistId"],
                this.metadata["projectId"],
                this.metadata["trackId"],
            );
            return track.name;
        } catch {
            return '[deleted]';
        }
    }

    async resolveIdeaName() {
        try {
            const idea = await Idea.getIdeaById(
                this.metadata["artistId"],
                this.metadata["projectId"],
                this.metadata["ideaId"],
            );
            return idea.name;
        } catch {
            return '[deleted]';
        }
    }

    async getLabelForTrackActivity() {
        const trackName = await this.resolveTrackName();
        switch (this.notificationType) {
        case "added-track":
            return `${this.getSenderName()} added a new track to ${this.project.name}: ${trackName}`;
        case "deleted-track":
            return `${this.getSenderName()} deleted a track from ${this.project.name}: ${trackName}`;
        case "renamed-track":
            return `${this.getSenderName()} renamed a track from ${this.metadata["prevName"]} to ${this.metadata["newName"]}`;
        case "added-revision-track":
            return `${this.getSenderName()} added a new version to ${trackName}`;
        case "deleted-revision-track":
            return `${this.getSenderName()} deleted a version from ${trackName}`;
        case "changed-revision-track":
            return `${this.getSenderName()} changed the current version of ${trackName}`;
        case "commented-track":
            return `${this.getSenderName()} commented on the track ${trackName}: ${this.metadata["commentRawString"]}`;
        default:
            return this.notificationType;
        }
    }

    async getLabelForIdeaActivity() {
        const projectName = this.project.name;
        const ideaName = await this.resolveIdeaName();
        switch (this.notificationType) {
            case "added-idea":
                return `${this.getSenderName()} added a new idea to ${projectName}: ${ideaName}`;
            case "deleted-idea":
                return `${this.getSenderName()} deleted an idea from ${projectName}: ${ideaName}`;
            case "renamed-idea":
                return `${this.getSenderName()} renamed an idea from ${this.metadata["prevName"]} to ${this.metadata["newName"]}`;
            default:
                return "DEFAULT";
        }
    }

    async getLabelForProjectActivity() {
        const projectName = this.project.name;
        
        switch (this.notificationType) {
            case "added-project":
                return `${this.getSenderName()} created the project ${projectName}`;
            case "deleted-project":
                return "DELETED PROJECT";
            case "renamed-project":
                return `${this.getSenderName()} renamed the project from ${this.metadata["prevName"]} to ${this.metadata["newName"]}`;
            case "added-project-album-art":
                return `${this.getSenderName()} added album art to ${projectName}`;
            case "updated-project-album-art":
                return `${this.getSenderName()} updated the album art for ${projectName}`;
            case "removed-project-album-art":
                return `${this.getSenderName()} removed the album art from ${projectName}`;
            default:
                return this.notificationType;
        }
    }

    /**
     * Fetches a snapshot of activities associated with the current instance.
     * 
     * @async
     * @function
     * @param {number} Z - The maximum number of activities to fetch. If set to -1, all activities will be fetched.
     * @returns {Promise<Array>} Returns a promise that resolves to an array of resolved activities.
     * @throws {Error} Throws an error if unable to fetch the snapshot or any other underlying error.
     * 
     * @example
     * 
     * const activities = await instanceOfYourClass.getSnapshotOfActivity(5);  // Fetches up to 5 activities.
     * const allActivities = await instanceOfYourClass.getSnapshotOfActivity(-1);  // Fetches all activities.
    */
    static async getActivityById(activityId) {
        const snapshot = await firebase.database().ref("activity").child(activityId).once("value");
        const activity = snapshot.val();
        const senderId = activity.metadata.senderId;
        const name = await User.getUserName(senderId);
        return new Activity(
            activityId, 
            activity["metadata"], 
            activity["notification type"], 
            activity["time created"],
            null,
            name
        );
    }
}

export default Activity;